import React, { useState, useEffect, useRef } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ChevronUp, PlusCircle } from "feather-icons-react/build/IconComponents";
import { useDispatch, useSelector } from "react-redux";
import { setToogleHeader } from "../../core/redux/action";
import AddCategoryList from "../../core/modals/inventory/addcategorylist";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import { useToast } from "../../Toaster/ToastContext";
import Loader from "../loader/loader";
import { apiClient } from "../../services/apiClient";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

const CategoryList = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const { showToast } = useToast();
  const hasFetched = useRef(false);
  const [permissions, setPermissions] = useState({
    create: false,
    read: false,
    update: false,
    delete: false,
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalCount: 0,
  });

  useEffect(() => {
    const storedPermissions = localStorage.getItem("rolespermissions");
    let parsedPermissions = [];
    try {
      const Permissions = JSON.parse(storedPermissions);
      const getPermissions = JSON.parse(Permissions.permissions);
      if (getPermissions.permissions) {
        parsedPermissions = getPermissions.permissions;
      } else {
        parsedPermissions = getPermissions;
      }

      const inventoryPermissions = parsedPermissions.find(
        (item) => item.title === "Inventory"
      );

      const productPermissions = inventoryPermissions?.childPage?.find(
        (child) => child.title === "Brands"
      );
      // console.log("search product", productPermissions)

      if (productPermissions) {
        setPermissions({
          create: productPermissions.create || false,
          read: productPermissions.read || false,
          update: productPermissions.update || false,
          delete: productPermissions.delete || false,
        });
      } else {
        console.error("Product permissions not found in localStorage");
        showToast(
          "Product permissions not found.",
          "error",
          "Permissions Error"
        );
      }
    } catch (error) {
      console.error("Error parsing permissions from localStorage:", error);
      showToast("Failed to load permissions.", "error", "Permissions Error");
    }
  }, []);

  const fetchCategories = async (currentPage = 1, pageSize = 20) => {
    setLoading(true);
    hasFetched.current = false;
    const pageIndex = currentPage - 1;
    try {
      const response = await apiClient.post("/paginate-category", {
        pageIndex,
        pageSize
      });
      const data = await response;
      if (data.success) {
        setCategories(data.data);
        setFilteredCategories(data.data);
        setPagination({
          currentPage: data.currentPage ?? currentPage,
          pageSize: data.pageSize ?? pageSize,
          totalPages: data.totalPages ?? 1,
          totalCount: data.totalCount || data.data.length,
        });
      } else {
        setCategories([]);
        setFilteredCategories([]);
      }
    } catch (error) {
      showToast(
        error.message || "Failed to fetch brands",
        "error",
        "Brands"
      );
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  };

  useEffect(() => {
    if (!hasFetched.current) {
      fetchCategories(pagination.currentPage, pagination.pageSize);
    }
  }, [pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
    hasFetched.current = false;
  }, []);

  const handleAddCategory = () => {
    setCurrentCategory(null);
  };

  const handleEditCategory = (category) => {
    setCurrentCategory(category);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setFilteredCategories(
      categories.filter(
        (category) =>
          category.name.toLowerCase().includes(term) ||
          category.slug.toLowerCase().includes(term) ||
          category.status.toLowerCase().includes(term) ||
          (category.image && category.image.toLowerCase().includes(term))
      )
    );
  };

  const handleToggleStatus = async (category) => {
    if (!permissions.update) {
      showToast(
        "You do not have permission to update brands.",
        "error",
        "Permissions Error"
      );
      return;
    }

    const newStatus = category.status === "active" ? "inactive" : "active";
    try {
      setLoading(true);
      const response = await apiClient.post("/store-category", {
        id: category.id,
        name: category.name,
        slug: category.slug,
        image: category.image,
        status: newStatus,
      });
      if (response.success) {
        showToast(
          `Brands status updated to ${newStatus}`,
          "success",
          "Brand Operation"
        );
        setCategories((prev) =>
          prev.map((cat) =>
            cat.id === category.id ? { ...cat, status: newStatus } : cat
          )
        );
        setFilteredCategories((prev) =>
          prev.map((cat) =>
            cat.id === category.id ? { ...cat, status: newStatus } : cat
          )
        );
      } else {
        showToast(
          response.message || "Failed to update brand status",
          "error",
          "Brand Operation"
        );
      }
    } catch (error) {
      showToast(
        error.message || "Failed to update brand status",
        "error",
        "Brand Operation"
      );
    } finally {
      setLoading(false);
    }
  };

  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  let columns = [
    {
      title: "Brand",
      dataIndex: "name",
      sorter: (a, b) => a.name.length - b.name.length,
      render: (text, record) => (
        <div className="d-flex align-items-center">
          {record.image ? (
            <img
              src={record.image}
              alt={text}
              loading="lazy"
              className="me-2"
              style={{
                width: "40px",
                height: "40px",
                objectFit: "cover",
                borderRadius: "4px",
              }}
            />
          ) : (
            <div
              className="me-2"
              style={{
                width: "40px",
                height: "40px",
                backgroundColor: "#f0f0f0",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#aaa",
                fontSize: "10px",
              }}
            >
              No Image
            </div>
          )}
          <span className="wrap-text">{text}</span>
        </div>
      ),
      width: 200,
    },
    {
      title: "Brand Slug",
      dataIndex: "slug",
      render: (text) => <span className="wrap-text">{text}</span>,
      sorter: (a, b) => a.slug.length - b.slug.length,
      width: 200,
    },
  ];

  // ✅ Show Status column only when read = true
  if (permissions.read) {
    columns.push({
      title: "Status",
      dataIndex: "status",
      render: (text, record) => (
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={text === "active"}
            onChange={() => handleToggleStatus(record)}
          />
          <span className="toggle-slider"></span>
        </label>
      ),
      sorter: (a, b) => a.status.length - b.status.length,
      width: 100,
    });
  }

  // ✅ Show Actions column only when read or delete = true
  if (permissions.read || permissions.delete) {
    columns.push({
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            {permissions.read && (
              <Link
                className="me-2 p-2"
                to="#"
                onClick={() => handleEditCategory(record)}
                data-bs-toggle="modal"
                data-bs-target="#add-category"
              >
                <i data-feather="edit" className="feather-edit"></i>
              </Link>
            )}
            {permissions.delete && (
              <Link
                className="confirm-text p-2"
                to="#"
                onClick={() => showConfirmationAlert(record)}
              >
                <i data-feather="trash-2" className="feather-trash-2"></i>
              </Link>
            )}
          </div>
        </div>
      ),
      width: 100,
    });
  }

  const MySwal = withReactContent(Swal);

  const showConfirmationAlert = (record) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      confirmButtonText: "Yes, delete it!",
      cancelButtonColor: "#ff0000",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          const response = await apiClient.post("/delete-category", {
            id: record.id,
          });
          const data = await response;
          if (data.success) {
            showToast(
              "Brand deleted successfully",
              "success",
              "Brand Operation!"
            );
            fetchCategories(pagination.currentPage, pagination.pageSize);
          } else {
            showToast(
              data.message || "Failed to delete brand",
              "error",
              "Brand Operation!"
            );
          }
        } catch (error) {
          showToast(
            error.message || "Failed to delete brand",
            "error",
            "Brand Operation!"
          );
        } finally {
          setLoading(false);
        }
      } else {
        MySwal.close();
      }
    });
  };

  const { currentPage, pageSize, totalPages, totalCount } = pagination;
  const startItem = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(startItem + pageSize - 1, totalCount);

  const pagesToShow = [];
  if (totalPages > 0) {
    if (totalPages <= 2) {
      for (let i = 1; i <= totalPages; i++) {
        pagesToShow.push(i);
      }
    } else {
      let middle = [];
      if (currentPage === 1) {
        middle = [1, 2];
      } else if (currentPage === totalPages) {
        middle = [totalPages - 1, totalPages];
      } else {
        middle = [currentPage, currentPage + 1];
      }

      if (middle[0] > 1) {
        pagesToShow.push(1);
      }
      if (middle[0] > 2) {
        pagesToShow.push("...");
      }
      middle.forEach((p) => pagesToShow.push(p));
      if (middle[1] < totalPages - 1) {
        pagesToShow.push("...");
      }
      if (middle[1] < totalPages) {
        pagesToShow.push(totalPages);
      }
    }
  }

  const changePage = (page) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
    }));
    hasFetched.current = false;
  };

  return (
    <div>
      <Loader loading={loading} />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Brand</h4>
                <h6>Manage your brands</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                  <Link
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    id="collapse-header"
                    className={data ? "active" : ""}
                    onClick={() => {
                      dispatch(setToogleHeader(!data));
                    }}
                  >
                    <ChevronUp />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
            <div className="page-btn">
              {permissions.create && (
                <Link
                  to="#"
                  className="btn btn-added"
                  onClick={handleAddCategory}
                  data-bs-toggle="modal"
                  data-bs-target="#add-category"
                >
                  <PlusCircle className="me-2" />
                  Add New Brand
                </Link>
              )}
            </div>
          </div>
          <div className="card table-list-card">
            <div className="card-body">
              <div className="table-top">
                <div className="search-set">
                  <div className="search-input">
                    <input
                      type="text"
                      placeholder="Search by Brand and Slug"
                      className="form-control form-control-sm formsearch"
                      onChange={handleSearch}
                    />
                    <Link to="#" className="btn btn-searchset">
                      <i data-feather="search" className="feather-search" />
                    </Link>
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={filteredCategories}
                  rowKey={(record) => record.id}
                  rowSelection={null}
                  pagination={false}
                />
              </div>

              {totalCount > 0 && (
                <div className="custom-pagination d-flex justify-content-between align-items-center mt-4 mb-4">
                  <div className="pagination-left">
                    Showing {startItem} - {endItem} of {totalCount}
                  </div>
                  <div className="pagination-right d-flex align-items-center">
                    <button
                      className="btn btn-sm btn-outline-secondary me-2"
                      disabled={currentPage === 1}
                      onClick={() => changePage(currentPage - 1)}
                    >
                      <LeftOutlined size={12} />
                    </button>
                    {pagesToShow.map((page, index) => (
                      <span key={index} className="me-2">
                        {page === "..." ? (
                          <span className="pagination-ellipsis">...</span>
                        ) : (
                          <button
                            className={`btn btn-sm ${page === currentPage ? "btn-primary" : "btn-outline-secondary"}`}
                            onClick={() => changePage(page)}
                          >
                            {page}
                          </button>
                        )}
                      </span>
                    ))}
                    <button
                      className="btn btn-sm btn-outline-secondary ms-2"
                      disabled={currentPage === totalPages}
                      onClick={() => changePage(currentPage + 1)}
                    >
                      <RightOutlined size={12} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <AddCategoryList
        category={currentCategory}
        onSave={() => {
          fetchCategories(pagination.currentPage, pagination.pageSize);
        }}
      />
    </div>
  );
};

export default CategoryList;
